import { ChangeEventHandler, forwardRef, useCallback } from 'react';

import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';

import {
  UKPhoneInputContainer,
  UKPhoneInputElement,
  UKPhoneInputPrefix,
  UKPhoneInputFlagIcon,
} from './UKPhoneInput.styles';

interface UKPhoneInputProps {
  name: string;
  placeholder?: string;
  value: string;
  onChange: (fullValue: string) => void;
  onBlur?: () => void;
  style?: React.CSSProperties;
}

function toLocalNumber(e164Value: string): string {
  if (!e164Value) return '';
  try {
    const parsed = parsePhoneNumber(e164Value, 'GB');
    return parsed?.nationalNumber || e164Value.replace(/^\+44/, '');
  } catch {
    return e164Value.replace(/^\+44/, '');
  }
}

function toE164(localValue: string): string {
  if (!localValue) return '';
  const cleaned = localValue.replace(/\s/g, '').replace(/^0+/, '');
  return `+44${cleaned}`;
}

export const UKPhoneInput = forwardRef<HTMLInputElement, UKPhoneInputProps>(
  ({ name, placeholder, value, onChange, onBlur, style }, ref) => {
    const displayValue = toLocalNumber(value);

    const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
      (e) => {
        const raw = e.target.value.replace(/[^\d\s]/g, '');
        onChange(raw ? toE164(raw) : '');
      },
      [onChange]
    );

    return (
      <UKPhoneInputContainer style={style}>
        <UKPhoneInputPrefix>
          <UKPhoneInputFlagIcon aria-label="United Kingdom">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30" width="24" height="12">
              <clipPath id="uk-clip">
                <rect width="60" height="30" />
              </clipPath>
              <g clipPath="url(#uk-clip)">
                <rect width="60" height="30" fill="#012169" />
                <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
                <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="4" clipPath="url(#uk-diag)" />
                <clipPath id="uk-diag">
                  <path d="M30,0 L60,0 L60,15 Z M30,30 L0,30 L0,15 Z M0,0 L0,10 L20,15 Z M60,30 L60,20 L40,15 Z" />
                </clipPath>
                <path d="M30,0 V30 M0,15 H60" stroke="#fff" strokeWidth="10" />
                <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="6" />
              </g>
            </svg>
          </UKPhoneInputFlagIcon>
          <span>+44</span>
        </UKPhoneInputPrefix>
        <UKPhoneInputElement
          ref={ref}
          name={name}
          type="tel"
          autoComplete="tel-national"
          inputMode="tel"
          placeholder={placeholder || 'Phone number'}
          value={displayValue}
          onChange={handleChange}
          onBlur={onBlur}
        />
      </UKPhoneInputContainer>
    );
  }
);

UKPhoneInput.displayName = 'UKPhoneInput';

export function validateUKPhoneNumber(value: string): string | true {
  if (!value) return true;
  if (!isValidPhoneNumber(value, 'GB')) {
    return 'Please enter a valid UK phone number';
  }
  return true;
}
